import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Empty,
  Progress,
  Segmented,
  Space,
  Spin,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  BulbOutlined,
  CheckOutlined,
  PictureOutlined,
  ReloadOutlined,
  SyncOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import type {
  MarketingCharacterAsset,
  MarketingCharacterRecord,
  MarketingInspirationItem,
  MarketingInspirationItemType,
  MarketingInspirationRun,
} from './types';
import { sortMarketingInspirationItems } from './inspiration';

const { Paragraph, Text, Title } = Typography;

interface MarketingSourceKitProps {
  apiBase: string;
  bookId: string;
  userId: string;
  sourceLanguage: string;
  editionLanguage: string;
  onManageSources: () => void;
  onCreateCharacter: () => void;
  onBrowseCharacterImages: () => void;
  onCreateFromSources: () => void;
  onSourcesChanged?: () => void;
  refreshSignal?: number;
}

interface CharacterResponse {
  characters?: MarketingCharacterRecord[];
}

type ReviewFilter = 'all' | MarketingInspirationItemType;
const REVIEW_BATCH_SIZE = 8;

const TYPE_LABELS: Record<MarketingInspirationItemType, string> = {
  scene: 'Scene',
  quote: 'Quote',
  vibe: 'Mood',
};

const TYPE_COLORS: Record<MarketingInspirationItemType, string> = {
  scene: 'blue',
  quote: 'gold',
  vibe: 'purple',
};

const readError = async (response: Response, fallback: string): Promise<string> => {
  try {
    const payload = await response.json();
    return String(payload?.detail || payload?.message || fallback);
  } catch {
    return fallback;
  }
};

const MarketingSourceKit: React.FC<MarketingSourceKitProps> = ({
  apiBase,
  bookId,
  userId,
  sourceLanguage,
  editionLanguage,
  onManageSources,
  onCreateCharacter,
  onBrowseCharacterImages,
  onCreateFromSources,
  onSourcesChanged,
  refreshSignal,
}) => {
  const [loading, setLoading] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const [updatingId, setUpdatingId] = useState('');
  const [items, setItems] = useState<MarketingInspirationItem[]>([]);
  const [runs, setRuns] = useState<MarketingInspirationRun[]>([]);
  const [characters, setCharacters] = useState<MarketingCharacterRecord[]>([]);
  const [characterAssets, setCharacterAssets] = useState<MarketingCharacterAsset[]>([]);
  const [characterError, setCharacterError] = useState('');
  const [reviewFilter, setReviewFilter] = useState<ReviewFilter>('all');
  const [visibleReviewCount, setVisibleReviewCount] = useState(REVIEW_BATCH_SIZE);

  const loadKit = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    const params = new URLSearchParams({ userId, sourceLanguage, editionLanguage });
    const sourceBase = `${apiBase}/api/books/${encodeURIComponent(bookId)}/marketing`;
    try {
      const [itemsResult, runsResult, charactersResult, assetsResult] = await Promise.allSettled([
        fetch(`${sourceBase}/inspiration?${params.toString()}`),
        fetch(`${sourceBase}/inspiration/runs?${params.toString()}`),
        fetch(`${sourceBase}/characters?${params.toString()}`),
        fetch(`${sourceBase}/characters/assets?${params.toString()}`),
      ]);

      if (itemsResult.status === 'fulfilled' && itemsResult.value.ok) {
        const payload = await itemsResult.value.json();
        setItems(sortMarketingInspirationItems(Array.isArray(payload?.items) ? payload.items : []));
      }
      if (runsResult.status === 'fulfilled' && runsResult.value.ok) {
        const payload = await runsResult.value.json();
        setRuns(Array.isArray(payload?.items) ? payload.items : []);
      }
      if (charactersResult.status === 'fulfilled' && charactersResult.value.ok) {
        const payload = (await charactersResult.value.json()) as CharacterResponse;
        setCharacters(Array.isArray(payload?.characters) ? payload.characters : []);
        setCharacterError('');
      } else if (charactersResult.status === 'fulfilled') {
        setCharacters([]);
        setCharacterError(await readError(
          charactersResult.value,
          'Character analysis needs to finish before character sources are available.'
        ));
      }
      if (assetsResult.status === 'fulfilled' && assetsResult.value.ok) {
        const payload = await assetsResult.value.json();
        setCharacterAssets(Array.isArray(payload?.items) ? payload.items : []);
      }
    } catch (error: any) {
      message.error(error?.message || 'Failed to load the Book Kit.');
    } finally {
      setLoading(false);
    }
  }, [apiBase, bookId, editionLanguage, sourceLanguage, userId]);

  useEffect(() => {
    void loadKit();
  }, [loadKit, refreshSignal]);

  const approvedItems = useMemo(
    () => items.filter(item => String(item.status || '').toLowerCase() === 'approved'),
    [items]
  );
  const reviewItems = useMemo(
    () => items.filter(item => {
      if (String(item.status || '').toLowerCase() !== 'draft') return false;
      return reviewFilter === 'all' || item.type === reviewFilter;
    }),
    [items, reviewFilter]
  );
  const draftTypeCounts = useMemo(
    () => ({
      all: items.filter(item => String(item.status || '').toLowerCase() === 'draft').length,
      scene: items.filter(item => String(item.status || '').toLowerCase() === 'draft' && item.type === 'scene').length,
      quote: items.filter(item => String(item.status || '').toLowerCase() === 'draft' && item.type === 'quote').length,
      vibe: items.filter(item => String(item.status || '').toLowerCase() === 'draft' && item.type === 'vibe').length,
    }),
    [items]
  );
  const visibleReviewItems = useMemo(
    () => reviewItems.slice(0, visibleReviewCount),
    [reviewItems, visibleReviewCount]
  );
  const remainingReviewCount = Math.max(0, reviewItems.length - visibleReviewItems.length);

  useEffect(() => {
    setVisibleReviewCount(REVIEW_BATCH_SIZE);
  }, [reviewFilter]);
  const activeRun = useMemo(
    () => runs.find(run => ['queued', 'running', 'retrying'].includes(String(run.status || '').toLowerCase())),
    [runs]
  );
  const typeCounts = useMemo(
    () => ({
      scene: approvedItems.filter(item => item.type === 'scene').length,
      quote: approvedItems.filter(item => item.type === 'quote').length,
      vibe: approvedItems.filter(item => item.type === 'vibe').length,
    }),
    [approvedItems]
  );
  const readinessChecks = [
    characters.length > 0,
    typeCounts.scene > 0,
    typeCounts.quote > 0 || typeCounts.vibe > 0,
  ];
  const readinessPercent = Math.round((readinessChecks.filter(Boolean).length / readinessChecks.length) * 100);

  const prepareKit = async () => {
    setPreparing(true);
    try {
      const response = await fetch(
        `${apiBase}/api/books/${encodeURIComponent(bookId)}/marketing/inspiration/extract`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId, sourceLanguage, editionLanguage }),
        }
      );
      if (!response.ok) throw new Error(await readError(response, 'Failed to prepare source material.'));
      message.success('Book Kit extraction queued. You can leave this page while it runs.');
      await loadKit();
    } catch (error: any) {
      message.error(error?.message || 'Failed to prepare the Book Kit.');
    } finally {
      setPreparing(false);
    }
  };

  const setItemStatus = async (item: MarketingInspirationItem, status: 'approved' | 'skipped') => {
    setUpdatingId(item.id);
    try {
      const response = await fetch(
        `${apiBase}/api/books/${encodeURIComponent(bookId)}/marketing/inspiration/items/${encodeURIComponent(item.id)}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId, status, sourceLanguage, editionLanguage }),
        }
      );
      if (!response.ok) throw new Error(await readError(response, 'Failed to update source material.'));
      const payload = await response.json();
      const nextItem = payload?.item as MarketingInspirationItem | undefined;
      setItems(current => sortMarketingInspirationItems(
        current.map(currentItem => currentItem.id === item.id && nextItem ? nextItem : currentItem)
      ));
      onSourcesChanged?.();
    } catch (error: any) {
      message.error(error?.message || 'Failed to update source material.');
    } finally {
      setUpdatingId('');
    }
  };

  return (
    <Spin spinning={loading}>
      <div className="marketing-source-kit">
        <section className="marketing-source-kit__hero">
          <div>
            <span className="marketing-source-kit__eyebrow">Book-wide foundation</span>
            <Title level={3}>Book Kit</Title>
            <Paragraph>
              Review the characters, scenes, exact quotes, and moods pulled from your manuscript once,
              Review passages discovered independently from the {editionLanguage} edition, then reuse
              approved sources in slides, videos, and images.
            </Paragraph>
          </div>
          <div className="marketing-source-kit__readiness">
            <Progress type="circle" size={72} percent={readinessPercent} />
            <div>
              <Text strong>{readinessPercent === 100 ? 'Ready to create' : 'Source kit in progress'}</Text>
              <Text type="secondary">{approvedItems.length} approved sources · {characters.length} characters</Text>
            </div>
          </div>
        </section>

        {activeRun ? (
          <Alert
            type="info"
            showIcon
            message="Source extraction is running"
            description="New scenes, quotes, and moods will appear here when the manuscript scan finishes."
            action={<Button size="small" icon={<ReloadOutlined />} onClick={() => void loadKit()}>Refresh</Button>}
          />
        ) : null}

        <div className="marketing-source-kit__summary-grid">
          <Card size="small" className="marketing-source-kit__summary-card">
            <Space direction="vertical" size={8}>
              <TeamOutlined className="marketing-source-kit__summary-icon" />
              <Text strong>Characters</Text>
              <Text type="secondary">
                {characters.length
                  ? `${characters.length} manuscript characters are available.`
                  : 'Character analysis is needed before visual references can be created.'}
              </Text>
              <Space wrap>
                <Tag color={characters.length ? 'green' : 'default'}>{characters.length} found</Tag>
                <Tag color={characterAssets.length ? 'blue' : 'default'}>{characterAssets.length} portraits</Tag>
              </Space>
              <Space wrap>
                <Button size="small" type="primary" onClick={onCreateCharacter}>Create portrait</Button>
                {characterAssets.length ? <Button size="small" onClick={onBrowseCharacterImages}>Browse</Button> : null}
              </Space>
            </Space>
          </Card>
          <Card size="small" className="marketing-source-kit__summary-card">
            <Space direction="vertical" size={8}>
              <BulbOutlined className="marketing-source-kit__summary-icon" />
              <Text strong>Story material</Text>
              <Text type="secondary">Approved sources are the safe defaults shown in creation tools.</Text>
              <Space wrap>
                <Tag color="blue">{typeCounts.scene} approved scenes</Tag>
                <Tag color="gold">{typeCounts.quote} quotes</Tag>
                <Tag color="purple">{typeCounts.vibe} moods</Tag>
                <Tag>{items.length} total suggestions</Tag>
              </Space>
              <Space wrap>
                <Button
                  size="small"
                  type="primary"
                  icon={<SyncOutlined />}
                  loading={preparing}
                  disabled={Boolean(activeRun)}
                  onClick={() => void prepareKit()}
                >
                  {items.length ? 'Scan manuscript again' : 'Prepare my Book Kit'}
                </Button>
                <Button size="small" onClick={onManageSources}>Manage all</Button>
              </Space>
            </Space>
          </Card>
        </div>

        {characterError ? (
          <Alert
            type="warning"
            showIcon
            message="Character sources are not ready"
            description={characterError}
            action={<Button size="small" onClick={onCreateCharacter}>View details</Button>}
          />
        ) : null}

        <section className="marketing-source-kit__review">
          <div className="marketing-source-kit__review-header">
            <div>
              <Title level={4}>Review suggestions</Title>
              <Paragraph>Approve useful material, skip weak suggestions, or open the full editor for precise control.</Paragraph>
            </div>
            <Segmented
              value={reviewFilter}
              onChange={value => setReviewFilter(value as ReviewFilter)}
              options={[
                { label: `All (${draftTypeCounts.all})`, value: 'all' },
                { label: `Scenes (${draftTypeCounts.scene})`, value: 'scene' },
                { label: `Quotes (${draftTypeCounts.quote})`, value: 'quote' },
                { label: `Moods (${draftTypeCounts.vibe})`, value: 'vibe' },
              ]}
            />
          </div>
          {reviewItems.length ? (
            <div className="marketing-source-kit__review-list">
              {visibleReviewItems.map(item => (
                <article key={item.id} className="marketing-source-kit__review-item">
                  <div className="marketing-source-kit__review-copy">
                    <Space wrap size={6}>
                      <Tag color={TYPE_COLORS[item.type]}>{TYPE_LABELS[item.type]}</Tag>
                      {item.spoilerLevel ? <Tag>Spoilers: {item.spoilerLevel}</Tag> : null}
                      {(item.characterNames || []).slice(0, 2).map(name => <Tag key={`${item.id}-${name}`}>{name}</Tag>)}
                    </Space>
                    <Text strong>{item.title || 'Untitled source'}</Text>
                    <Paragraph ellipsis={{ rows: 4 }}>
                      {item.sourceExcerpt || item.quoteText || item.text}
                    </Paragraph>
                    {item.authorSynopsis && item.editionKind === 'translation' ? (
                      <Text type="secondary">{item.authorSynopsis}</Text>
                    ) : null}
                    {item.selectionRationale ? (
                      <Text type="secondary">Why it works: {item.selectionRationale}</Text>
                    ) : null}
                    {item.recommended ? <Tag color="green">AI recommended</Tag> : null}
                    {item.sourceVerified ? <Tag>Verified {editionLanguage} text</Tag> : null}
                  </div>
                  <Space wrap>
                    <Button
                      type="primary"
                      icon={<CheckOutlined />}
                      loading={updatingId === item.id}
                      onClick={() => void setItemStatus(item, 'approved')}
                    >
                      Approve
                    </Button>
                    <Button
                      loading={updatingId === item.id}
                      onClick={() => void setItemStatus(item, 'skipped')}
                    >
                      Skip
                    </Button>
                  </Space>
                </article>
              ))}
            </div>
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={items.length
                ? 'No draft suggestions match this filter.'
                : 'Prepare the Book Kit to build a campaign-ready bank of scenes, exact quotes, and moods for story slides, carousels, character art, and videos.'}
            />
          )}
          {reviewItems.length ? (
            <div className="marketing-source-kit__review-progress" role="status" aria-live="polite">
              <div>
                <Text strong>Showing {visibleReviewItems.length} of {reviewItems.length} suggestions</Text>
                <Text type="secondary">
                  {remainingReviewCount
                    ? `${remainingReviewCount} more available in this filter.`
                    : 'All suggestions in this filter are shown.'}
                </Text>
              </div>
              <Space wrap>
                {visibleReviewCount > REVIEW_BATCH_SIZE ? (
                  <Button size="small" onClick={() => setVisibleReviewCount(REVIEW_BATCH_SIZE)}>Show fewer</Button>
                ) : null}
                {remainingReviewCount ? (
                  <Button
                    size="small"
                    type="primary"
                    onClick={() => setVisibleReviewCount(count => count + REVIEW_BATCH_SIZE)}
                  >
                    Show {Math.min(REVIEW_BATCH_SIZE, remainingReviewCount)} more
                  </Button>
                ) : null}
              </Space>
            </div>
          ) : null}
          <div className="marketing-source-kit__review-footer">
            <Button onClick={onManageSources}>Open advanced source manager</Button>
            <Button
              type="primary"
              icon={<PictureOutlined />}
              disabled={!approvedItems.length}
              onClick={onCreateFromSources}
            >
              Create from approved sources
            </Button>
          </div>
        </section>
      </div>
    </Spin>
  );
};

export default MarketingSourceKit;
